import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Globe } from "lucide-react";

/* ─── COLORS ─── */
export const C = {
  navy: "#29294d",
  orange: "#fa9000",
  orangeRed: "#f64e0e",
  olive: "#81801a",
  red: "#f64e0e",
  white: "#FFFFFF",
  offwhite: "#f0eee9",
  blue: "#686af3",
  lilac: "#a88efb",
  pink: "#da818d",
  black: "#1a1a1a",
  gray: "#64748b",
  lightgray: "#94a3b8",
  bg: "#f8fafc",
};

/* ─── FONTS ─── */
export const DISPLAY = "'DM Serif Display', 'Ravenna', Georgia, serif";
export const BODY = "'Albert Sans', 'Ardena', system-ui, sans-serif";
export const MONO = "'IBM Plex Mono', monospace";

/* ─── EMOJI SAFEGUARD (contenido creado por usuarios) ─── */
export const stripEmojis = (s) =>
  typeof s === "string"
    ? s.replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}\u{20E3}]/gu, "").replace(/ {2,}/g, " ").trim()
    : s;

/* ─── REVEAL (estático: sin animación de opacidad ligada al scroll) ─── */
export function Reveal({ children, delay = 0, className = "" }) {
  return <div className={className}>{children}</div>;
}

/* ─── EDITORIAL LABEL ─── */
export function EditorialLabel({ number, text, color = C.orange }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      {number && <span className="text-xs font-semibold tracking-widest" style={{ fontFamily: MONO, color }}>{number}</span>}
      <span className="block w-10 h-px" style={{ background: color }} />
      <span className="text-[11px] font-semibold tracking-[0.2em] uppercase" style={{ fontFamily: MONO, color }}>{text}</span>
    </div>
  );
}

/* ─── EDITORIAL PAGE HEADER ─── */
export function EditorialHeader({ backLabel = "Volver", backTo = "/" }) {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-50 px-6 md:px-12 py-4"
      style={{ background: C.white, borderBottom: `1px solid ${C.navy}15` }}
      data-testid="page-header"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between">
        <button onClick={() => navigate(backTo)} className="flex items-center gap-2 text-sm font-semibold"
          style={{ fontFamily: BODY, color: C.blue, background: "none", border: "none", cursor: "pointer" }}
          data-testid="page-back-btn"
        ><ArrowLeft className="w-4 h-4" /> {backLabel}</button>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="flex items-center gap-2"
            style={{ background: "none", border: "none", cursor: "pointer" }}>
            <img src="/logos/logo-emergencia-animal.png" alt="EA" className="w-8 h-8 object-contain" />
            <span className="text-sm font-bold tracking-tight hidden sm:block" style={{ fontFamily: BODY, color: C.navy }}>
              Emergencia Animal
            </span>
          </button>
        </div>
        <nav className="hidden md:flex items-center gap-1">
          {[
            { l: "Guías", h: "/recursos/ciudadania/guias" },
            { l: "Consultoría", h: "/consultoria" },
          ].map((lnk) => (
            <button key={lnk.l} onClick={() => navigate(lnk.h)}
              className="px-3 py-2 text-sm font-medium"
              style={{ fontFamily: BODY, color: C.navy, opacity: 0.6, background: "none", border: "none", cursor: "pointer" }}
            >{lnk.l}</button>
          ))}
          <button onClick={() => navigate("/select-country")}
            className="px-3 py-2 text-sm font-medium flex items-center gap-1"
            style={{ fontFamily: BODY, color: C.blue, background: "none", border: "none", cursor: "pointer" }}
          ><Globe className="w-3.5 h-3.5" /> País</button>
        </nav>
      </div>
    </header>
  );
}

/* ─── EDITORIAL BUTTON ─── */
export function EBtn({ children, onClick, bg = C.orange, color = C.navy, outline = false, href, disabled, testId, className = "", type = "button" }) {
  const base = "inline-flex items-center gap-2 px-7 py-3 text-sm font-bold transition-all hover:-translate-y-0.5";
  const style = outline
    ? { fontFamily: BODY, background: "transparent", color: bg, border: `1px solid ${bg}40`, cursor: "pointer", borderRadius: 4 }
    : { fontFamily: BODY, background: bg, color, border: "none", cursor: "pointer", borderRadius: 4, opacity: disabled ? 0.5 : 1 };
  return (
    <button onClick={onClick} data-testid={testId} className={`${base} ${className}`} style={style} type={type} disabled={disabled}>
      {children}
    </button>
  );
}

/* ─── EDITORIAL SECTION ─── */
export function Section({ children, bg = C.white, className = "", id, testId, noPad = false }) {
  return (
    <section id={id} className={`${noPad ? "" : "px-6 md:px-12 py-16 md:py-24"} ${className}`} style={{ background: bg }} data-testid={testId}>
      <div className="max-w-[1400px] mx-auto">{children}</div>
    </section>
  );
}

/* ─── EDITORIAL HEADING ─── */
export function Heading({ children, size = "lg", className = "" }) {
  const sizes = {
    xl: "text-[clamp(2.5rem,6vw,5rem)]",
    lg: "text-[clamp(2rem,5vw,3.5rem)]",
    md: "text-[clamp(1.5rem,3vw,2.5rem)]",
    sm: "text-xl md:text-2xl",
  };
  return (
    <h2 className={`${sizes[size]} leading-[1.05] tracking-tight ${className}`} style={{ fontFamily: DISPLAY, color: C.navy }}>
      {children}
    </h2>
  );
}
